/**
 * Sentry helpers for remote debugging.
 *
 * Isolated from debug.js so that any Sentry import issues
 * cannot break the core debug logger.
 */

let Sentry = null

// Lazy-load Sentry only when first needed
function getSentry() {
  if (Sentry !== null) return Sentry
  try {
    // @sentry/react is already loaded in main.jsx via static import.
    // We access it lazily here so that if it ever fails to load,
    // it won't take down other modules that import this file.
    Sentry = window.__SENTRY_HUB__ ? true : false
  } catch {
    Sentry = false
  }
  return Sentry
}

/**
 * Send a Sentry warning event with extras — use for anomaly detection.
 * Safe to call even if Sentry is not configured (becomes a no-op).
 */
export function captureWarning(message, extras, tags) {
  try {
    // Dynamic import so this never blocks module loading
    import('@sentry/react').then(S => {
      if (!S.isInitialized()) return
      S.withScope(scope => {
        if (extras) scope.setExtras(extras)
        if (tags) Object.entries(tags).forEach(([k, v]) => scope.setTag(k, v))
        S.captureMessage(message, 'warning')
      })
    }).catch(() => {})
  } catch { /* ignore */ }
}

/**
 * Capture an exception with extras and tags. Accepts an Error or any value
 * (non-Errors are wrapped). Safe to call even if Sentry is not configured.
 *
 * Tagging events with `flow: 'payment'` ensures they bypass the
 * network-error filter in main.jsx beforeSend, so payment failures
 * always reach Sentry even when the underlying cause is a network blip.
 */
export function captureError(error, extras, tags) {
  try {
    import('@sentry/react').then(S => {
      if (!S.isInitialized()) return
      S.withScope(scope => {
        if (extras) scope.setExtras(extras)
        if (tags) Object.entries(tags).forEach(([k, v]) => scope.setTag(k, v))
        const err = error instanceof Error ? error : new Error(String(error?.message ?? error))
        S.captureException(err)
      })
    }).catch(() => {})
  } catch { /* ignore */ }
}
